import { TodoList2 } from '@griply/assets';
import { ALL_TASKS_LINKED_ITEM_ID, ID, LinkedItemType } from '@griply/types';
import { resolveCalendarSyncTarget } from '@griply/utils';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IconFormIcons, Target } from 'shared/assets/icons';
import { GoalTreeContextMenu } from 'shared/components/connected/goal-context-menu';
import { Size as SelectSize } from 'shared/components/ui/custom-form-select';
import { FormSelectTrigger } from 'shared/components/ui/form-select-trigger';
import { Icon } from 'shared/components/ui/icon';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';

import * as Styled from './calendar-sync-options.style';

export type CalendarSyncLinkedItemSelectProps = {
  linkedItemId: ID | null;
  goals: Goal[];
  lifeAreas: LifeArea[];
  disabled?: boolean;
  onChange: (value: { id: ID; type: LinkedItemType }) => void;
};

// The "which tasks" trigger: a goal/life-area tree behind a select surface, shared by the
// configured row and the add dialog so both resolve and label a target the same way.
export const CalendarSyncLinkedItemSelect: React.FC<
  CalendarSyncLinkedItemSelectProps
> = ({ linkedItemId, goals, lifeAreas, disabled, onChange }) => {
  const { t } = useTranslation();
  const linkedItemRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const allTasksLabel = t('calendar.sync-tasks.all-tasks');

  const target = useMemo(
    () => resolveCalendarSyncTarget(linkedItemId, { goals, lifeAreas }),
    [goals, lifeAreas, linkedItemId],
  );
  const { isAllTasks, isGoal } = target;

  // `name === null` is the resolver's "not found" signal; an empty-but-real name must still
  // count as resolved, or a nameless goal would render as an unset row.
  const isResolved = target.name !== null;

  const currentSelection = useMemo(() => {
    if (!linkedItemId || !isResolved) {
      return undefined;
    }
    return isGoal ? { goalId: linkedItemId } : { lifeAreaId: linkedItemId };
  }, [linkedItemId, isResolved, isGoal]);

  // The tree reports a target container: a clicked goal arrives with its id as the last
  // `parentIds` entry; a clicked life area arrives via `lifeAreaId`.
  const onSelectLinkedItem = (parentIds: ID[], lifeAreaId: ID | null) => {
    if (parentIds.length) {
      onChange({
        id: parentIds[parentIds.length - 1],
        type: LinkedItemType.Goal,
      });
      closeMenu();
      return;
    }
    if (lifeAreaId) {
      onChange({ id: lifeAreaId, type: LinkedItemType.LifeArea });
    }
    closeMenu();
  };

  const onSelectAllTasks = () => {
    onChange({
      id: ALL_TASKS_LINKED_ITEM_ID,
      type: LinkedItemType.AllTasks,
    });
    closeMenu();
  };

  const linkedItemIcon = isAllTasks
    ? TodoList2
    : target.iconName
      ? IconFormIcons[target.iconName]
      : Target;

  return (
    <div ref={linkedItemRef}>
      <FormSelectTrigger
        onClick={openMenu}
        isPlaceholder={!isAllTasks && !isResolved}
        size={SelectSize.ExtraSmall}
        noOverflow
        disabled={disabled}
        ariaHasPopup="dialog"
        ariaExpanded={menuOpen}
      >
        <Styled.SelectValue>
          <Styled.SelectIcon $color={target.color ?? undefined}>
            <Icon icon={linkedItemIcon} />
          </Styled.SelectIcon>
          <Styled.SelectLabel>
            {isAllTasks
              ? allTasksLabel
              : target.name ?? t('calendar.sync-tasks.placeholder')}
          </Styled.SelectLabel>
        </Styled.SelectValue>
      </FormSelectTrigger>

      {menuOpen && (
        <GoalTreeContextMenu
          hideNoLifeArea
          onSelect={onSelectLinkedItem}
          currentSelection={currentSelection}
          allTasksLabel={allTasksLabel}
          isAllTasksSelected={isAllTasks}
          onSelectAllTasks={onSelectAllTasks}
          referenceElement={linkedItemRef}
          position="bottom"
          onClose={closeMenu}
        />
      )}
    </div>
  );
};
